import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';
import {
	List,
} from 'immutable';

import GraphQL from '~/types/graphql';

import AttachedIcon from '~/components/patterns/structuredValues/AttachedIcon';
import BasicIcon, {
	BasicIconType,
} from '~/components/patterns/icons/BasicIcon';
import {
	BenefitIconType,
} from '~/components/patterns/icons/BenefitIcon';
import HighlightedValue from '~/components/patterns/values/HighlightedValue';

import {
	BENEFIT_COBRANDING,
	BENEFIT_CORE_WEB_VITALS_ORIGIN_SUMMARY,
	BENEFIT_CUSTOM_ALERT_TRIGGER,
	BENEFIT_CUSTOM_ELEMENTS_0,
	BENEFIT_CUSTOM_ELEMENTS_10,
	BENEFIT_CUSTOM_ELEMENTS_2,
	BENEFIT_CUSTOM_ELEMENTS_5,
	BENEFIT_DATA_RETENTION_12_MONTHS,
	BENEFIT_DATA_RETENTION_6_MONTHS,
	BENEFIT_DATA_RETENTION_9_MONTHS,
	BENEFIT_GOOGLE_LOOKER_STUDIO,
	BENEFIT_ISSUES_CONFIGURATION,
	BENEFIT_LIGHTHOUSE_MONITORING,
	BENEFIT_LOG_FILE_ANALYSIS,
	BENEFIT_MICROSOFT_TEAMS,
	BENEFIT_REALTIME_INDEX_NOW,
	BENEFIT_RENDERING,
	BENEFIT_REPORTING_API,
	BENEFIT_UNLIMITED_USERS,
	BENEFIT_UNLIMITED_WEBSITES,
	SECTION_ACCESS_CONTROL,
	SECTION_ADVANCED_ANALYSIS,
	SECTION_ADVANCED_FEATURES,
	SECTION_COLLABORATION,
	SECTION_CORE_FEATURES,
	SECTION_INTEGRATIONS,
	SECTION_MONITORING_SETTINGS,
	SECTION_SERVICES,
} from './benefits';

import {
	comparePlans as _comparePlans,
} from './plans';

import {
	notEmpty,
} from '~/utilities/typeCheck';



const benefitValueMessages = defineMessages({
	addon: {
		id: 'ui.planBenefits.values.addon',
	},
	headersFullBodyAddon: {
		id: 'ui.planBenefits.values.headersFullBodyAddon',
	},
	headersOnly: {
		id: 'ui.planBenefits.values.headersOnly',
	},
	monthsCount: {
		id: 'ui.planBenefits.values.monthsCount',
	},
	nonStandard: {
		id: 'ui.planBenefits.values.nonStandard',
	},
	perWebsite: {
		id: 'ui.planBenefits.values.perWebsite',
	},
	priority: {
		id: 'ui.planBenefits.values.priority',
	},
	standard: {
		id: 'ui.planBenefits.values.standard',
	},
	unlimited: {
		id: 'ui.planBenefits.values.unlimited',
	},
	upToMonthsCount: {
		id: 'ui.planBenefits.values.upToMonthsCount',
	},
	upToPerWebsite: {
		id: 'ui.planBenefits.values.upToPerWebsite',
	},
});

const messages = defineMessages({
	comingSoon: {
		id: 'ui.general.comingSoon',
	},
});

const hintMessages = defineMessages({
	lightspeedMonitoring: {
		id: 'ui.planBenefits.section.monitoringSettings.benefitHints.lightspeedMonitoring',
	},
	rechecks: {
		id: 'ui.planBenefits.section.coreFeatures.benefitHints.rechecks',
	},
});



export const LIST_OF_ALL_PLANS = {
	c1: List([
		GraphQL.AccountPlan.EnterpriseLite,
		GraphQL.AccountPlan.Enterprise,
	]),
	v3: List([
		GraphQL.AccountPlan.Basic,
		GraphQL.AccountPlan.Standard,
		GraphQL.AccountPlan.Pro,
		GraphQL.AccountPlan.Prov4,
		GraphQL.AccountPlan.Enterprise,
	]),
	v4: List([
		GraphQL.AccountPlan.Basic,
		GraphQL.AccountPlan.Standard,
		GraphQL.AccountPlan.Pro,
		GraphQL.AccountPlan.Enterprise,
	]),
};

export const PLAN_BENEFITS = {
	v3: {
		[GraphQL.AccountPlan.Basic]: [
			{
				detailed: true,
				name: BENEFIT_UNLIMITED_WEBSITES,
			},
			{
				detailed: true,
				name: BENEFIT_UNLIMITED_USERS,
			},
			{
				name: BENEFIT_DATA_RETENTION_6_MONTHS,
			},
			{
				name: BENEFIT_COBRANDING,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.IssuesConfiguration,
				name: BENEFIT_ISSUES_CONFIGURATION,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.CustomElements_2,
				name: BENEFIT_CUSTOM_ELEMENTS_0,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.WebVitalsOriginSummary,
				name: BENEFIT_CORE_WEB_VITALS_ORIGIN_SUMMARY,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.LighthouseMonitoring,
				name: BENEFIT_LIGHTHOUSE_MONITORING,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.CustomAlertTrigger,
				name: BENEFIT_CUSTOM_ALERT_TRIGGER,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.Rendering,
				name: BENEFIT_RENDERING,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.ReportingApi,
				name: BENEFIT_GOOGLE_LOOKER_STUDIO,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.ReportingApi,
				name: BENEFIT_REPORTING_API,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.LogFileAnalysis,
				name: BENEFIT_LOG_FILE_ANALYSIS,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.IndexNow,
				name: BENEFIT_REALTIME_INDEX_NOW,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.MicrosoftTeams,
				name: BENEFIT_MICROSOFT_TEAMS,
			},
		],
		[GraphQL.AccountPlan.Standard]: [
			{
				detailed: true,
				name: BENEFIT_UNLIMITED_WEBSITES,
			},
			{
				detailed: true,
				name: BENEFIT_UNLIMITED_USERS,
			},
			{
				name: BENEFIT_DATA_RETENTION_9_MONTHS,
			},
			{
				name: BENEFIT_COBRANDING,
			},
			{
				feature: GraphQL.AccountFeature.IssuesConfiguration,
				name: BENEFIT_ISSUES_CONFIGURATION,
			},
			{
				feature: GraphQL.AccountFeature.CustomElements_2,
				name: BENEFIT_CUSTOM_ELEMENTS_2,
			},
			{
				feature: GraphQL.AccountFeature.WebVitalsOriginSummary,
				name: BENEFIT_CORE_WEB_VITALS_ORIGIN_SUMMARY,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.LighthouseMonitoring,
				name: BENEFIT_LIGHTHOUSE_MONITORING,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.CustomAlertTrigger,
				name: BENEFIT_CUSTOM_ALERT_TRIGGER,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.Rendering,
				name: BENEFIT_RENDERING,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.ReportingApi,
				name: BENEFIT_GOOGLE_LOOKER_STUDIO,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.ReportingApi,
				name: BENEFIT_REPORTING_API,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.LogFileAnalysis,
				name: BENEFIT_LOG_FILE_ANALYSIS,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.IndexNow,
				name: BENEFIT_REALTIME_INDEX_NOW,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.MicrosoftTeams,
				name: BENEFIT_MICROSOFT_TEAMS,
			},
		],
		[GraphQL.AccountPlan.Pro]: [
			{
				detailed: true,
				name: BENEFIT_UNLIMITED_WEBSITES,
			},
			{
				detailed: true,
				name: BENEFIT_UNLIMITED_USERS,
			},
			{
				name: BENEFIT_DATA_RETENTION_12_MONTHS,
			},
			{
				name: BENEFIT_COBRANDING,
			},
			{
				feature: GraphQL.AccountFeature.IssuesConfiguration,
				name: BENEFIT_ISSUES_CONFIGURATION,
			},
			{
				feature: GraphQL.AccountFeature.CustomElements_5,
				name: BENEFIT_CUSTOM_ELEMENTS_5,
			},
			{
				feature: GraphQL.AccountFeature.WebVitalsOriginSummary,
				name: BENEFIT_CORE_WEB_VITALS_ORIGIN_SUMMARY,
			},
			{
				feature: GraphQL.AccountFeature.LighthouseMonitoring,
				name: BENEFIT_LIGHTHOUSE_MONITORING,
			},
			{
				feature: GraphQL.AccountFeature.CustomAlertTrigger,
				name: BENEFIT_CUSTOM_ALERT_TRIGGER,
			},
			{
				feature: GraphQL.AccountFeature.Rendering,
				name: BENEFIT_RENDERING,
			},
			{
				feature: GraphQL.AccountFeature.ReportingApi,
				name: BENEFIT_GOOGLE_LOOKER_STUDIO,
			},
			{
				feature: GraphQL.AccountFeature.ReportingApi,
				name: BENEFIT_REPORTING_API,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.LogFileAnalysis,
				name: BENEFIT_LOG_FILE_ANALYSIS,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.IndexNow,
				name: BENEFIT_REALTIME_INDEX_NOW,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.MicrosoftTeams,
				name: BENEFIT_MICROSOFT_TEAMS,
			},
		],
		[GraphQL.AccountPlan.Prov4]: [
			{
				detailed: true,
				name: BENEFIT_UNLIMITED_WEBSITES,
			},
			{
				detailed: true,
				name: BENEFIT_UNLIMITED_USERS,
			},
			{
				name: BENEFIT_DATA_RETENTION_12_MONTHS,
			},
			{
				name: BENEFIT_COBRANDING,
			},
			{
				feature: GraphQL.AccountFeature.IssuesConfiguration,
				name: BENEFIT_ISSUES_CONFIGURATION,
			},
			{
				feature: GraphQL.AccountFeature.CustomElements_10,
				name: BENEFIT_CUSTOM_ELEMENTS_10,
			},
			{
				feature: GraphQL.AccountFeature.WebVitalsOriginSummary,
				name: BENEFIT_CORE_WEB_VITALS_ORIGIN_SUMMARY,
			},
			{
				feature: GraphQL.AccountFeature.LighthouseMonitoring,
				name: BENEFIT_LIGHTHOUSE_MONITORING,
			},
			{
				feature: GraphQL.AccountFeature.CustomAlertTrigger,
				name: BENEFIT_CUSTOM_ALERT_TRIGGER,
			},
			{
				feature: GraphQL.AccountFeature.Rendering,
				name: BENEFIT_RENDERING,
			},
			{
				feature: GraphQL.AccountFeature.ReportingApi,
				name: BENEFIT_GOOGLE_LOOKER_STUDIO,
			},
			{
				feature: GraphQL.AccountFeature.ReportingApi,
				name: BENEFIT_REPORTING_API,
			},
			{
				feature: GraphQL.AccountFeature.LogFileAnalysis,
				name: BENEFIT_LOG_FILE_ANALYSIS,
			},
			{
				feature: GraphQL.AccountFeature.IndexNow,
				name: BENEFIT_REALTIME_INDEX_NOW,
			},
			{
				feature: GraphQL.AccountFeature.MicrosoftTeams,
				name: BENEFIT_MICROSOFT_TEAMS,
			},
		],
	},
	v4: {
		[GraphQL.AccountPlan.Basic]: [
			{
				detailed: true,
				name: BENEFIT_UNLIMITED_WEBSITES,
			},
			{
				detailed: true,
				name: BENEFIT_UNLIMITED_USERS,
			},
			{
				name: BENEFIT_DATA_RETENTION_6_MONTHS,
			},
			{
				name: BENEFIT_COBRANDING,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.CustomElements_2,
				name: BENEFIT_CUSTOM_ELEMENTS_0,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.WebVitalsOriginSummary,
				name: BENEFIT_CORE_WEB_VITALS_ORIGIN_SUMMARY,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.LighthouseMonitoring,
				name: BENEFIT_LIGHTHOUSE_MONITORING,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.CustomAlertTrigger,
				name: BENEFIT_CUSTOM_ALERT_TRIGGER,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.Rendering,
				name: BENEFIT_RENDERING,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.ReportingApi,
				name: BENEFIT_GOOGLE_LOOKER_STUDIO,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.ReportingApi,
				name: BENEFIT_REPORTING_API,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.LogFileAnalysis,
				name: BENEFIT_LOG_FILE_ANALYSIS,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.IndexNow,
				name: BENEFIT_REALTIME_INDEX_NOW,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.MicrosoftTeams,
				name: BENEFIT_MICROSOFT_TEAMS,
			},
		],
		[GraphQL.AccountPlan.Standard]: [
			{
				detailed: true,
				name: BENEFIT_UNLIMITED_WEBSITES,
			},
			{
				detailed: true,
				name: BENEFIT_UNLIMITED_USERS,
			},
			{
				name: BENEFIT_DATA_RETENTION_9_MONTHS,
			},
			{
				name: BENEFIT_COBRANDING,
			},
			{
				feature: GraphQL.AccountFeature.CustomElements_5,
				name: BENEFIT_CUSTOM_ELEMENTS_5,
			},
			{
				feature: GraphQL.AccountFeature.WebVitalsOriginSummary,
				name: BENEFIT_CORE_WEB_VITALS_ORIGIN_SUMMARY,
			},
			{
				feature: GraphQL.AccountFeature.LighthouseMonitoring,
				name: BENEFIT_LIGHTHOUSE_MONITORING,
			},
			{
				feature: GraphQL.AccountFeature.CustomAlertTrigger,
				name: BENEFIT_CUSTOM_ALERT_TRIGGER,
			},
			{
				feature: GraphQL.AccountFeature.Rendering,
				name: BENEFIT_RENDERING,
			},
			{
				feature: GraphQL.AccountFeature.ReportingApi,
				name: BENEFIT_GOOGLE_LOOKER_STUDIO,
			},
			{
				feature: GraphQL.AccountFeature.ReportingApi,
				name: BENEFIT_REPORTING_API,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.LogFileAnalysis,
				name: BENEFIT_LOG_FILE_ANALYSIS,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.IndexNow,
				name: BENEFIT_REALTIME_INDEX_NOW,
			},
			{
				disabled: true,
				feature: GraphQL.AccountFeature.MicrosoftTeams,
				name: BENEFIT_MICROSOFT_TEAMS,
			},
		],
		[GraphQL.AccountPlan.Pro]: [
			{
				detailed: true,
				name: BENEFIT_UNLIMITED_WEBSITES,
			},
			{
				detailed: true,
				name: BENEFIT_UNLIMITED_USERS,
			},
			{
				name: BENEFIT_DATA_RETENTION_12_MONTHS,
			},
			{
				name: BENEFIT_COBRANDING,
			},
			{
				feature: GraphQL.AccountFeature.CustomElements_10,
				name: BENEFIT_CUSTOM_ELEMENTS_10,
			},
			{
				feature: GraphQL.AccountFeature.WebVitalsOriginSummary,
				name: BENEFIT_CORE_WEB_VITALS_ORIGIN_SUMMARY,
			},
			{
				feature: GraphQL.AccountFeature.LighthouseMonitoring,
				name: BENEFIT_LIGHTHOUSE_MONITORING,
			},
			{
				feature: GraphQL.AccountFeature.CustomAlertTrigger,
				name: BENEFIT_CUSTOM_ALERT_TRIGGER,
			},
			{
				feature: GraphQL.AccountFeature.Rendering,
				name: BENEFIT_RENDERING,
			},
			{
				feature: GraphQL.AccountFeature.ReportingApi,
				name: BENEFIT_GOOGLE_LOOKER_STUDIO,
			},
			{
				feature: GraphQL.AccountFeature.ReportingApi,
				name: BENEFIT_REPORTING_API,
			},
			{
				feature: GraphQL.AccountFeature.LogFileAnalysis,
				name: BENEFIT_LOG_FILE_ANALYSIS,
			},
			{
				feature: GraphQL.AccountFeature.IndexNow,
				name: BENEFIT_REALTIME_INDEX_NOW,
			},
			{
				feature: GraphQL.AccountFeature.MicrosoftTeams,
				name: BENEFIT_MICROSOFT_TEAMS,
			},
		],
	},
};

export function getNewPlanBenefits(
	accountFeaturesAvailability: ReadonlyArray<{
		accountFeature: GraphQL.AccountFeature,
		plan: GraphQL.AccountPlan,
		status: GraphQL.AccountFeatureAvailabilityStatus,
		tariff: string,
	}>,
	section:
		| typeof SECTION_ACCESS_CONTROL
		| typeof SECTION_ADVANCED_ANALYSIS
		| typeof SECTION_ADVANCED_FEATURES
		| typeof SECTION_COLLABORATION
		| typeof SECTION_CORE_FEATURES
		| typeof SECTION_INTEGRATIONS
		| typeof SECTION_MONITORING_SETTINGS
		| typeof SECTION_SERVICES,
) {
	return NEW_PLAN_BENEFITS[section].map((benefit) => {
		if (benefit.values === undefined) {
			benefit.values = {};

			LIST_OF_ALL_PLANS.v4.toArray().forEach((plan) => {
				const color = plan === GraphQL.AccountPlan.Enterprise
					? `#f4a21e`
					: undefined;

				let isAvailable = false;

				if (benefit.featureForAll) {
					isAvailable = true;
				} else if (benefit.featureName) {
					isAvailable = isFeatureAvailable(
						accountFeaturesAvailability,
						'v4',
						plan,
						benefit.featureName,
					);
				} else if (benefit.plans) {
					isAvailable = benefit.plans.includes(plan);
				}

				benefit.values[plan] = (
					<BasicIcon
						color={color}
						size={18}
						type={isAvailable ? BasicIconType.Available : BasicIconType.Unavailable}
					/>
				);
			});
		}

		return benefit;
	});
}

const NEW_PLAN_BENEFITS = {
	[SECTION_ACCESS_CONTROL]: [
		{
			featureName: GraphQL.AccountFeature.AdminRole,
			iconType: undefined,
			name: 'organizationAdminRole',
		},
		{
			featureName: GraphQL.AccountFeature.EmailDomainsWhitelist,
			iconType: undefined,
			name: 'restrictEmailDomains',
		},
		{
			featureName: GraphQL.AccountFeature.EnforceTwoFactorAuthentication,
			iconType: undefined,
			name: 'enforce2FA',
		},
		{
			iconType: undefined,
			name: 'auditLog',
			values: {
				[GraphQL.AccountPlan.Basic]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Standard]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Pro]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Enterprise]: (
					<HighlightedValue tag={true}>
						<FormattedMessage {...messages.comingSoon} />
					</HighlightedValue>
				),
			},
		},
	],
	[SECTION_ADVANCED_ANALYSIS]: [
		{
			plans: [
				GraphQL.AccountPlan.Standard,
				GraphQL.AccountPlan.Pro,
				GraphQL.AccountPlan.Enterprise,
			],
			iconType: undefined,
			name: 'httpHeaderSnapshot',
		},
		{
			featureName: GraphQL.AccountFeature.StoreResponseSnapshots,
			iconType: undefined,
			name: 'httpBodySnapshot',
		},
		{
			featureName: GraphQL.AccountFeature.LogFileAnalysis,
			iconType: undefined,
			name: 'logFileAnalysis',
		},
		{
			iconType: undefined,
			name: 'logFileExplorer',
			values: {
				[GraphQL.AccountPlan.Basic]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Standard]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Pro]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Enterprise]: (
					<HighlightedValue tag={true}>
						<FormattedMessage {...messages.comingSoon} />
					</HighlightedValue>
				),
			},
		},
	],
	[SECTION_ADVANCED_FEATURES]: [
		{
			iconType: undefined,
			name: 'dataRetention',
			values: {
				[GraphQL.AccountPlan.Basic]: (
					<FormattedMessage
						{...benefitValueMessages.monthsCount}
						tagName="span"
						values={{
							b: (value) => (
								<strong>{value}</strong>
							),
							count: 6,
						}}
					/>
				),
				[GraphQL.AccountPlan.Standard]: (
					<FormattedMessage
						{...benefitValueMessages.monthsCount}
						tagName="span"
						values={{
							b: (value) => (
								<strong>{value}</strong>
							),
							count: 9,
						}}
					/>
				),
				[GraphQL.AccountPlan.Pro]: (
					<FormattedMessage
						{...benefitValueMessages.monthsCount}
						tagName="span"
						values={{
							b: (value) => (
								<strong>{value}</strong>
							),
							count: 12,
						}}
					/>
				),
				[GraphQL.AccountPlan.Enterprise]: (
					<FormattedMessage
						{...benefitValueMessages.upToMonthsCount}
						tagName="span"
						values={{
							b: (value) => (
								<strong>{value}</strong>
							),
							count: 60,
						}}
					/>
				),
			},
		},
		{
			iconType: undefined,
			name: 'customElementExtraction',
			values: {
				[GraphQL.AccountPlan.Basic]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Standard]: (
					<AttachedIcon
						columnFlow={true}
						icon={(
							<BasicIcon
								size={18}
								type={BasicIconType.Available}
							/>
						)}
					>
						<FormattedMessage
							{...benefitValueMessages.perWebsite}
							values={{
								b: (value) => (
									<strong>{value}</strong>
								),
								count: 5,
							}}
						/>
					</AttachedIcon>
				),
				[GraphQL.AccountPlan.Pro]: (
					<AttachedIcon
						columnFlow={true}
						icon={(
							<BasicIcon
								size={18}
								type={BasicIconType.Available}
							/>
						)}
					>
						<FormattedMessage
							{...benefitValueMessages.perWebsite}
							values={{
								b: (value) => (
									<strong>{value}</strong>
								),
								count: 10,
							}}
						/>
					</AttachedIcon>
				),
				[GraphQL.AccountPlan.Enterprise]: (
					<AttachedIcon
						columnFlow={true}
						ellipsis={false}
						icon={(
							<BasicIcon
								color="#f4a21e"
								size={18}
								type={BasicIconType.Available}
							/>
						)}
					>
						<FormattedMessage
							{...benefitValueMessages.upToPerWebsite}
							values={{
								b: (value) => (
									<strong>{value}</strong>
								),
								count: 20,
							}}
						/>
					</AttachedIcon>
				),
			},
		},
		{
			featureName: GraphQL.AccountFeature.CustomAlertTrigger,
			iconType: undefined,
			name: 'alertsForCustomElements',
		},
		{
			featureName: GraphQL.AccountFeature.Rendering,
			iconType: BasicIconType.JavaScript,
			name: 'jsRendering',
		},
		{
			featureName: GraphQL.AccountFeature.WebVitalsOriginSummary,
			iconType: BasicIconType.CoreWebVitalsMonitoring,
			name: 'coreWebVitalsMonitoring',
		},
		{
			featureName: GraphQL.AccountFeature.IndexNow,
			iconType: BasicIconType.RealtimeIndexNow,
			name: 'realTimeIndexNow',
		},
	],
	[SECTION_COLLABORATION]: [
		{
			featureForAll: true,
			iconType: undefined,
			name: 'cobranding',
		},
		{
			featureForAll: true,
			iconType: undefined,
			name: 'clientAccess',
		},
	],
	[SECTION_CORE_FEATURES]: [
		{
			iconType: undefined,
			name: 'users',
			values: {
				[GraphQL.AccountPlan.Basic]: (
					<FormattedMessage {...benefitValueMessages.unlimited} />
				),
				[GraphQL.AccountPlan.Standard]: (
					<FormattedMessage {...benefitValueMessages.unlimited} />
				),
				[GraphQL.AccountPlan.Pro]: (
					<FormattedMessage {...benefitValueMessages.unlimited} />
				),
				[GraphQL.AccountPlan.Enterprise]: (
					<FormattedMessage {...benefitValueMessages.unlimited} />
				),
			},
		},
		{
			hint: (
				<FormattedMessage {...hintMessages.rechecks} />
			),
			iconType: undefined,
			name: 'rechecks',
			values: {
				[GraphQL.AccountPlan.Basic]: (
					<FormattedMessage {...benefitValueMessages.unlimited} />
				),
				[GraphQL.AccountPlan.Standard]: (
					<FormattedMessage {...benefitValueMessages.unlimited} />
				),
				[GraphQL.AccountPlan.Pro]: (
					<FormattedMessage {...benefitValueMessages.unlimited} />
				),
				[GraphQL.AccountPlan.Enterprise]: (
					<FormattedMessage {...benefitValueMessages.unlimited} />
				),
			},
		},
		{
			featureForAll: true,
			iconType: undefined,
			name: 'websiteAuditing',
		},
		{
			featureForAll: true,
			iconType: undefined,
			name: 'nonStopMonitoring',
		},
		{
			featureForAll: true,
			iconType: undefined,
			name: 'changeTracking',
		},
		{
			featureForAll: true,
			iconType: undefined,
			name: 'alerting',
		},
	],
	[SECTION_INTEGRATIONS]: [
		{
			featureForAll: true,
			iconType: BasicIconType.Slack,
			name: 'slack',
		},
		{
			featureForAll: true,
			iconType: BasicIconType.GoogleAnalytics,
			name: 'googleAnalytics',
		},
		{
			featureForAll: true,
			iconType: BasicIconType.GoogleSearchConsole,
			name: 'googleSearchConsole',
		},
		{
			featureForAll: true,
			iconType: BasicIconType.Chrome,
			name: 'googleChromeExtension',
		},
		{
			featureName: GraphQL.AccountFeature.ReportingApi,
			iconType: BasicIconType.LookerStudio,
			name: 'googleLookerStudio',
		},
		{
			featureName: GraphQL.AccountFeature.ReportingApi,
			iconType: BasicIconType.Api,
			name: 'reportingAPI',
		},
		{
			featureName: GraphQL.AccountFeature.MicrosoftTeams,
			iconType: BasicIconType.MicrosoftTeams,
			name: 'microsoftTeams',
		},
		{
			featureName: GraphQL.AccountFeature.EnrichmentFields_5,
			iconType: BasicIconType.Api,
			name: 'dataEnrichmentAPI',
		},
		{
			featureName: GraphQL.AccountFeature.AdobeAnalyticsIntegration,
			iconType: BasicIconType.AdobeAnalytics,
			name: 'adobeAnalytics',
		},
	],
	[SECTION_MONITORING_SETTINGS]: [
		{
			featureForAll: true,
			iconType: undefined,
			name: 'monitoringFromUSUKEU',
		},
		{
			iconType: undefined,
			name: 'customMonitoringLocation',
			values: {
				[GraphQL.AccountPlan.Basic]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Standard]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Pro]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Enterprise]: (
					<AttachedIcon
						columnFlow={true}
						icon={(
							<BasicIcon
								color="#f4a21e"
								size={18}
								type={BasicIconType.Available}
							/>
						)}
					>
						<FormattedMessage {...benefitValueMessages.addon} />
					</AttachedIcon>
				),
			},
		},
		{
			featureName: GraphQL.AccountFeature.HighCrawlingSpeed,
			hint: (
				<FormattedMessage {...hintMessages.lightspeedMonitoring} />
			),
			iconType: undefined,
			name: 'lightspeedMonitoring',
		},
	],
	[SECTION_SERVICES]: [
		{
			iconType: undefined,
			name: 'chatSupport',
			values: {
				[GraphQL.AccountPlan.Basic]: (
					<FormattedMessage {...benefitValueMessages.standard} />
				),
				[GraphQL.AccountPlan.Standard]: (
					<FormattedMessage {...benefitValueMessages.standard} />
				),
				[GraphQL.AccountPlan.Pro]: (
					<FormattedMessage {...benefitValueMessages.standard} />
				),
				[GraphQL.AccountPlan.Enterprise]: (
					<AttachedIcon
						ellipsis={false}
						icon={(
							<BasicIcon
								color="#f4a21e"
								size={24}
								type={BasicIconType.Priority}
							/>
						)}
					>
						<FormattedMessage {...benefitValueMessages.priority} />
					</AttachedIcon>
				),
			},
		},
		{
			iconType: undefined,
			name: 'emailSupport',
			values: {
				[GraphQL.AccountPlan.Basic]: (
					<FormattedMessage {...benefitValueMessages.standard} />
				),
				[GraphQL.AccountPlan.Standard]: (
					<FormattedMessage {...benefitValueMessages.standard} />
				),
				[GraphQL.AccountPlan.Pro]: (
					<FormattedMessage {...benefitValueMessages.standard} />
				),
				[GraphQL.AccountPlan.Enterprise]: (
					<AttachedIcon
						ellipsis={false}
						icon={(
							<BasicIcon
								color="#f4a21e"
								size={24}
								type={BasicIconType.Priority}
							/>
						)}
					>
						<FormattedMessage {...benefitValueMessages.priority} />
					</AttachedIcon>
				),
			},
		},
		{
			iconType: undefined,
			name: 'guidesAndVideos',
			values: {
				[GraphQL.AccountPlan.Basic]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Available}
					/>
				),
				[GraphQL.AccountPlan.Standard]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Available}
					/>
				),
				[GraphQL.AccountPlan.Pro]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Available}
					/>
				),
				[GraphQL.AccountPlan.Enterprise]: (
					<BasicIcon
						color="#f4a21e"
						size={18}
						type={BasicIconType.Available}
					/>
				),
			},
		},
		{
			iconType: undefined,
			name: 'customTraining',
			values: {
				[GraphQL.AccountPlan.Basic]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Standard]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Pro]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Enterprise]: (
					<AttachedIcon
						columnFlow={true}
						icon={(
							<BasicIcon
								color="#f4a21e"
								size={18}
								type={BasicIconType.Available}
							/>
						)}
					>
						<FormattedMessage {...benefitValueMessages.addon} />
					</AttachedIcon>
				),
			},
		},
		{
			iconType: undefined,
			name: 'phoneSupport',
			values: {
				[GraphQL.AccountPlan.Basic]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Standard]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Pro]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Enterprise]: (
					<BasicIcon
						color="#f4a21e"
						size={18}
						type={BasicIconType.Available}
					/>
				),
			},
		},
		{
			iconType: undefined,
			name: 'dedicatedSuccessManager',
			values: {
				[GraphQL.AccountPlan.Basic]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Standard]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Pro]: (
					<BasicIcon
						size={18}
						type={BasicIconType.Unavailable}
					/>
				),
				[GraphQL.AccountPlan.Enterprise]: (
					<BasicIcon
						color="#f4a21e"
						size={18}
						type={BasicIconType.Available}
					/>
				),
			},
		},
	],
};



export function getEnterprisePlanBenefits({ accountIsOutOfBand = false }) {
	return [
		!accountIsOutOfBand ? {
			name: 'unlimitedUsersWebsitesRechecks',
			iconType: BenefitIconType.UnlimitedUsersWebsitesRechecks,
		} : null,
		accountIsOutOfBand ? {
			name: 'unlimitedUsersRechecks',
			iconType: BenefitIconType.UnlimitedUsersWebsitesRechecks,
		} : null,
		{
			name: 'realtimeAuditing',
			iconType: BenefitIconType.RealtimeAuditing,
		},
		{
			name: 'changeTrackingAndAlerting',
			iconType: BenefitIconType.ChangeTrackingAndAlerting,
		},
		{
			name: 'bodySnapshots',
			iconType: BenefitIconType.BodySnapshots,
		},
		{
			name: 'logFileAnalysis',
			iconType: BenefitIconType.LogFileAnalysis,
		},
		{
			name: 'CWVMonitoringAndJSRendering',
			iconType: BenefitIconType.CWVMonitoringAndJSRendering,
		},
		{
			name: 'dedicatedSuccessManager',
			iconType: BenefitIconType.DedicatedSuccessManager,
		},
		{
			name: 'onboardingAndCustomTraining',
			iconType: BenefitIconType.OnboardingAndCustomTraining,
		},
		{
			name: 'prioritySupport',
			iconType: BenefitIconType.PrioritySupport,
		},
		{
			name: 'customElements',
			iconType: BenefitIconType.CustomElements,
		},
		{
			name: 'dataRetention',
			iconType: BenefitIconType.DataRetention,
		},
		{
			name: 'analyticsAndGSCIntegrations',
			iconType: BenefitIconType.AnalyticsAndGSCIntegrations,
		},
		{
			name: 'dataStudioAndAPIs',
			iconType: BenefitIconType.DataStudioAndAPIs,
		},
		{
			name: 'chatPlatformIntegrations',
			iconType: BenefitIconType.ChatPlatformIntegrations,
		},
		{
			name: 'customMonitoringLocation',
			iconType: BenefitIconType.CustomMonitoringLocation,
		},
		{
			name: 'invoiceBilling',
			iconType: BenefitIconType.InvoiceBilling,
		},
		{
			name: 'adminAndAccessControlFeatures',
			iconType: BenefitIconType.AdminAndAccessControlFeatures,
		},
	].filter(notEmpty);
}



export function comparePlans(previous, next) {
	return _comparePlans([
		GraphQL.AccountPlan.Basic,
		GraphQL.AccountPlan.Standard,
		GraphQL.AccountPlan.Pro,
		GraphQL.AccountPlan.Prov4,
		GraphQL.AccountPlan.EnterpriseLite,
		GraphQL.AccountPlan.Enterprise,
	], previous, next);
}



export function isFeatureAvailable(
	accountFeaturesAvailability: ReadonlyArray<{
		accountFeature: GraphQL.AccountFeature,
		plan: GraphQL.AccountPlan,
		status: GraphQL.AccountFeatureAvailabilityStatus,
		tariff: string,
	}>,
	tariff: string,
	plan: GraphQL.AccountPlan,
	featureName: GraphQL.AccountFeature,
) {
	const featureAvailability = accountFeaturesAvailability.find(
		(item) => (
			item.accountFeature === featureName
			&& item.plan === plan
			&& item.tariff === tariff
		),
	);

	if (featureAvailability === undefined) {
		return true;
	}

	return featureAvailability.status === GraphQL.AccountFeatureAvailabilityStatus.Available;
}



export function getPlanAvailabilityModern(
	accountFeaturesAvailability: ReadonlyArray<{
		accountFeature: GraphQL.AccountFeature,
		plan: GraphQL.AccountPlan,
		status: GraphQL.AccountFeatureAvailabilityStatus,
		tariff: string,
	}>,
	accountPlan: GraphQL.AccountPlan,
	accountTariff: string,
	plan: GraphQL.AccountPlan,
	usedFeatures: ReadonlyArray<GraphQL.AccountFeature>,
) {
	if (accountPlan === plan) {
		return {
			conflictingFeatures: [],
			isAvailable: true,
		};
	}

	const hierarchy = {
		[GraphQL.AccountFeature.CustomElements_5]: [
			GraphQL.AccountFeature.CustomElements_2,
		],
		[GraphQL.AccountFeature.CustomElements_10]: [
			GraphQL.AccountFeature.CustomElements_2,
			GraphQL.AccountFeature.CustomElements_5,
		],
		[GraphQL.AccountFeature.CustomElements_15]: [
			GraphQL.AccountFeature.CustomElements_2,
			GraphQL.AccountFeature.CustomElements_5,
			GraphQL.AccountFeature.CustomElements_10,
		],
		[GraphQL.AccountFeature.CustomElements_20]: [
			GraphQL.AccountFeature.CustomElements_2,
			GraphQL.AccountFeature.CustomElements_5,
			GraphQL.AccountFeature.CustomElements_10,
			GraphQL.AccountFeature.CustomElements_15,
		],
		[GraphQL.AccountFeature.CustomElements_25]: [
			GraphQL.AccountFeature.CustomElements_2,
			GraphQL.AccountFeature.CustomElements_5,
			GraphQL.AccountFeature.CustomElements_10,
			GraphQL.AccountFeature.CustomElements_15,
			GraphQL.AccountFeature.CustomElements_20,
		],
		[GraphQL.AccountFeature.CustomElements_30]: [
			GraphQL.AccountFeature.CustomElements_2,
			GraphQL.AccountFeature.CustomElements_5,
			GraphQL.AccountFeature.CustomElements_10,
			GraphQL.AccountFeature.CustomElements_15,
			GraphQL.AccountFeature.CustomElements_20,
			GraphQL.AccountFeature.CustomElements_25,
		],
		[GraphQL.AccountFeature.CustomElements_35]: [
			GraphQL.AccountFeature.CustomElements_2,
			GraphQL.AccountFeature.CustomElements_5,
			GraphQL.AccountFeature.CustomElements_10,
			GraphQL.AccountFeature.CustomElements_15,
			GraphQL.AccountFeature.CustomElements_20,
			GraphQL.AccountFeature.CustomElements_25,
			GraphQL.AccountFeature.CustomElements_30,
		],
		[GraphQL.AccountFeature.CustomElements_40]: [
			GraphQL.AccountFeature.CustomElements_2,
			GraphQL.AccountFeature.CustomElements_5,
			GraphQL.AccountFeature.CustomElements_10,
			GraphQL.AccountFeature.CustomElements_15,
			GraphQL.AccountFeature.CustomElements_20,
			GraphQL.AccountFeature.CustomElements_25,
			GraphQL.AccountFeature.CustomElements_30,
			GraphQL.AccountFeature.CustomElements_35,
		],
		[GraphQL.AccountFeature.EnrichmentFields_10]: [
			GraphQL.AccountFeature.EnrichmentFields_5,
		],
		[GraphQL.AccountFeature.EnrichmentFields_15]: [
			GraphQL.AccountFeature.EnrichmentFields_5,
			GraphQL.AccountFeature.EnrichmentFields_10,
		],
		[GraphQL.AccountFeature.EnrichmentFields_20]: [
			GraphQL.AccountFeature.EnrichmentFields_5,
			GraphQL.AccountFeature.EnrichmentFields_10,
			GraphQL.AccountFeature.EnrichmentFields_15,
		],
		[GraphQL.AccountFeature.EnrichmentFields_25]: [
			GraphQL.AccountFeature.EnrichmentFields_5,
			GraphQL.AccountFeature.EnrichmentFields_10,
			GraphQL.AccountFeature.EnrichmentFields_15,
			GraphQL.AccountFeature.EnrichmentFields_20,
		],
		[GraphQL.AccountFeature.EnrichmentFields_30]: [
			GraphQL.AccountFeature.EnrichmentFields_5,
			GraphQL.AccountFeature.EnrichmentFields_10,
			GraphQL.AccountFeature.EnrichmentFields_15,
			GraphQL.AccountFeature.EnrichmentFields_20,
			GraphQL.AccountFeature.EnrichmentFields_25,
		],
		[GraphQL.AccountFeature.EnrichmentFields_35]: [
			GraphQL.AccountFeature.EnrichmentFields_5,
			GraphQL.AccountFeature.EnrichmentFields_10,
			GraphQL.AccountFeature.EnrichmentFields_15,
			GraphQL.AccountFeature.EnrichmentFields_20,
			GraphQL.AccountFeature.EnrichmentFields_25,
			GraphQL.AccountFeature.EnrichmentFields_30,
		],
		[GraphQL.AccountFeature.EnrichmentFields_40]: [
			GraphQL.AccountFeature.EnrichmentFields_5,
			GraphQL.AccountFeature.EnrichmentFields_10,
			GraphQL.AccountFeature.EnrichmentFields_15,
			GraphQL.AccountFeature.EnrichmentFields_20,
			GraphQL.AccountFeature.EnrichmentFields_25,
			GraphQL.AccountFeature.EnrichmentFields_30,
			GraphQL.AccountFeature.EnrichmentFields_35,
		],
	};

	let conflictingFeatures = [
		GraphQL.AccountFeature.Rendering,
		GraphQL.AccountFeature.CustomElements_2,
		GraphQL.AccountFeature.CustomElements_5,
		GraphQL.AccountFeature.CustomElements_10,
		GraphQL.AccountFeature.CustomElements_15,
		GraphQL.AccountFeature.CustomElements_20,
		GraphQL.AccountFeature.CustomElements_25,
		GraphQL.AccountFeature.CustomElements_30,
		GraphQL.AccountFeature.CustomElements_35,
		GraphQL.AccountFeature.CustomElements_40,
		GraphQL.AccountFeature.CustomAlertTrigger,
		GraphQL.AccountFeature.EnrichmentFields_5,
		GraphQL.AccountFeature.EnrichmentFields_10,
		GraphQL.AccountFeature.EnrichmentFields_15,
		GraphQL.AccountFeature.EnrichmentFields_20,
		GraphQL.AccountFeature.EnrichmentFields_25,
		GraphQL.AccountFeature.EnrichmentFields_30,
		GraphQL.AccountFeature.EnrichmentFields_35,
		GraphQL.AccountFeature.EnrichmentFields_40,
		GraphQL.AccountFeature.MicrosoftTeams,
		GraphQL.AccountFeature.ReportingApi,
		GraphQL.AccountFeature.LighthouseMonitoring,
		GraphQL.AccountFeature.LogFileAnalysis,
		GraphQL.AccountFeature.WebVitalsOriginSummary,
		GraphQL.AccountFeature.AdminRole,
		GraphQL.AccountFeature.IssuesConfiguration,
	].filter((featureName) => (
		!isFeatureAvailable(accountFeaturesAvailability, accountTariff, plan, featureName)
		&& usedFeatures.includes(featureName)
	));

	conflictingFeatures = conflictingFeatures.filter((featureName) => (
		!hierarchy[featureName]
		|| !hierarchy[featureName].some((overridingFeatureName) => conflictingFeatures.includes(overridingFeatureName))
	));

	return {
		conflictingFeatures,
		isAvailable: conflictingFeatures.length === 0,
	};
}



export function findHighestPlan(plans) {
	return [
		GraphQL.AccountPlan.Enterprise,
		GraphQL.AccountPlan.Prov4,
		GraphQL.AccountPlan.Pro,
		GraphQL.AccountPlan.Standard,
		GraphQL.AccountPlan.Basic,
	].find((plan) => plans.includes(plan)) ?? null;
}
